import { useMemo, useState } from 'react';
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
  ToggleButton,
  makeStyles,
} from '@fluentui/react-components';
import { FilterIcon } from '@fluentui/react-icons-mdl2';
import { FilterForm } from './FilterForm';
import { FilterTag } from './FilterTag';
import { emptyFilterFor } from './filter.utils';
import type { IActiveFilter, IFilterableColumn } from './filter.types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
  },
});

interface DataGridFiltersProps {
  columns: IFilterableColumn[];
  filters: IActiveFilter[];
  onApply: (filter: IActiveFilter) => void;
  onUpdate: (index: number, filter: IActiveFilter) => void;
  onRemove: (index: number) => void;
  /** When true the "Add filter" button is hidden (an external add button is expected) */
  compact?: boolean;
}

const ADD_MODE = -1;

export const DataGridFilters = ({
  columns,
  filters,
  onApply,
  onUpdate,
  onRemove,
  compact = false,
}: DataGridFiltersProps) => {
  const styles = useStyles();
  // null = closed, -1 = add mode, >= 0 = editing filter at index
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const orderedIndexes = useMemo(
    () =>
      filters
        .map((_, i) => i)
        .sort(
          (a, b) =>
            Number(!!filters[b].column.isDefault) -
            Number(!!filters[a].column.isDefault)
        ),
    [filters]
  );

  const isAddOpen = openIndex === ADD_MODE;

  const handleApply = (filter: IActiveFilter) => {
    onApply(filter);
    setOpenIndex(null);
  };

  const handleUpdate = (filter: IActiveFilter) => {
    if (openIndex !== null && openIndex >= 0) {
      onUpdate(openIndex, filter);
      setOpenIndex(null);
    }
  };

  const handleClose = () => setOpenIndex(null);

  return (
    <div className={styles.container}>
      {orderedIndexes.map((index) => {
        const filter = filters[index];
        const isDefault = !!filter.column.isDefault;
        return (
          <Popover
            key={filter.column.key}
            open={openIndex === index}
            onOpenChange={(_, data) => {
              if (!data.open) setOpenIndex(null);
            }}
            positioning="below"
          >
            <PopoverTrigger disableButtonEnhancement>
              <div>
                <FilterTag
                  filter={filter}
                  onClick={() => setOpenIndex(index)}
                  onRemove={isDefault ? undefined : () => onRemove(index)}
                  onReset={
                    isDefault
                      ? () => onUpdate(index, emptyFilterFor(filter.column))
                      : undefined
                  }
                />
              </div>
            </PopoverTrigger>
            <PopoverSurface>
              <FilterForm
                columns={columns}
                onSubmit={handleUpdate}
                onCancel={handleClose}
                title="Edit filter"
                submitText="Save"
                initialView="configure-filter"
                initialFilter={filter}
                disableFilterSelect
              />
            </PopoverSurface>
          </Popover>
        );
      })}

      {!compact && (
        <Popover
          open={isAddOpen}
          onOpenChange={(_, data) => setOpenIndex(data.open ? ADD_MODE : null)}
          positioning="below"
        >
          <PopoverTrigger disableButtonEnhancement>
            <ToggleButton
              size="small"
              shape="circular"
              icon={<FilterIcon style={{ fontSize: 12 }} />}
              checked={isAddOpen}
              aria-label="Add filter"
            >
              Add filter
            </ToggleButton>
          </PopoverTrigger>
          <PopoverSurface>
            <FilterForm
              columns={columns.filter(
                (c) => !filters.some((f) => f.column.key === c.key)
              )}
              onSubmit={handleApply}
              onCancel={handleClose}
            />
          </PopoverSurface>
        </Popover>
      )}
    </div>
  );
};
